import { useState, useRef, useEffect } from "react";
import { Drama, Send, ClipboardCheck, RotateCcw, Mic } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAppMode } from "@/context/AppModeContext";
import { useSpeechInput, speak } from "@/lib/voice";
import { Loader, Bullets, SectionCard } from "@/components/common";

const SCENARIOS = {
  adult: [
    { role: "Job Interviewer", scenario: "A job interview for a role you want", emoji: "💼" },
    { role: "Your Manager", scenario: "You're asking your manager for a raise", emoji: "📈" },
    { role: "Angry Customer", scenario: "A customer is complaining about a late order", emoji: "😤" },
    { role: "Investor", scenario: "You're pitching your startup idea to an investor", emoji: "🚀" },
    { role: "Landlord", scenario: "Negotiating your rent with a landlord", emoji: "🏠" },
  ],
  child: [
    { role: "New Classmate", scenario: "Making a new friend on the playground", emoji: "🧒" },
    { role: "Teacher", scenario: "Asking your teacher for help politely", emoji: "🍎" },
    { role: "A Stranger", scenario: "A stranger you don't know talks to you — practice staying safe", emoji: "🛡️" },
  ],
  senior: [
    { role: "Bank Caller", scenario: "Someone calls claiming to be from your bank asking for details — practice staying safe", emoji: "📞" },
    { role: "Family Member", scenario: "Asking a family member to help you learn something on your phone", emoji: "👪" },
    { role: "Shop Assistant", scenario: "Returning a faulty product to a shop", emoji: "🛍️" },
  ],
};

export default function Practice() {
  const { mode, isSenior, isChild } = useAppMode();
  const list = SCENARIOS[mode] || SCENARIOS.adult;
  const [sc, setSc] = useState(null);
  const [history, setHistory] = useState([]);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const endRef = useRef(null);

  const { listening, supported, start } = useSpeechInput((t) => setMsg(t));

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [history, loading]);

  const startScenario = (s) => { setSc(s); setHistory([]); setFeedback(null); };

  const send = async () => {
    if (!msg.trim() || loading) return;
    const mine = { from: "you", text: msg.trim() };
    const next = [...history, mine];
    setHistory(next); setMsg(""); setLoading(true);
    try {
      const { data } = await api.post("/simulate/turn", { role: sc.role, scenario: sc.scenario, mode, history: next, message: mine.text });
      const reply = data.reply || "…";
      setHistory((h) => [...h, { from: sc.role, text: reply }]);
      if (isSenior || isChild) speak(reply);
    } catch { toast.error("Could not get a reply."); }
    finally { setLoading(false); }
  };

  const getFeedback = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/simulate/feedback", { role: sc.role, scenario: sc.scenario, mode, history, message: "" });
      setFeedback(data.error ? null : data);
    } catch { toast.error("Could not analyse."); }
    finally { setLoading(false); }
  };

  if (!sc) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="font-head font-extrabold text-3xl sm:text-4xl flex items-center gap-3"><Drama className="w-8 h-8 text-primary" />Practice & Simulate</h1>
          <p className="text-muted-foreground mt-2">Rehearse real conversations with an AI that plays the other person — then get honest feedback.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((s) => (
            <button key={s.role} onClick={() => startScenario(s)} data-testid={`scenario-${s.role.replace(/\s/g,'-').toLowerCase()}`}
              className="text-left rounded-lg border border-border bg-card p-5 hover:border-primary hover:-translate-y-0.5 transition-all">
              <div className="text-3xl">{s.emoji}</div>
              <div className="font-head font-bold text-lg mt-2">{s.role}</div>
              <div className="text-sm text-muted-foreground">{s.scenario}</div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-3xl mx-auto">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-head font-extrabold text-2xl">{sc.role}</h1>
          <p className="text-sm text-muted-foreground">{sc.scenario}</p>
        </div>
        <button onClick={() => setSc(null)} data-testid="practice-exit" className="inline-flex items-center gap-1.5 text-sm rounded-full border border-border px-3 py-2 hover:bg-secondary"><RotateCcw className="w-4 h-4" />New</button>
      </div>

      <div className="rounded-lg border border-border bg-card p-4 min-h-[300px] max-h-[420px] overflow-y-auto scrollbar-thin space-y-3" data-testid="practice-chat">
        {history.length === 0 && <p className="text-muted-foreground text-sm text-center py-10">Say something to begin. You're practising — there are no wrong answers.</p>}
        {history.map((m, i) => (
          <div key={i} className={`flex ${m.from === "you" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${m.from === "you" ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-secondary text-secondary-foreground rounded-bl-sm"}`}>
              {m.from !== "you" && <div className="text-[10px] uppercase tracking-wide opacity-70 mb-0.5">{sc.role}</div>}
              {m.text}
            </div>
          </div>
        ))}
        {loading && !feedback && <Loader label="…" />}
        <div ref={endRef} />
      </div>

      <div className="flex items-center gap-2">
        {supported && <button onClick={start} data-testid="practice-mic" className={`h-11 w-11 shrink-0 grid place-items-center rounded-full border ${listening ? "bg-destructive text-destructive-foreground animate-pulse" : "border-border hover:border-primary"}`}><Mic className="w-5 h-5" /></button>}
        <input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="practice-input"
          placeholder="Type your response…" className="flex-1 rounded-full border border-input bg-card px-4 py-3 outline-none focus:ring-2 focus:ring-ring" />
        <button onClick={send} disabled={loading} data-testid="practice-send" className="h-11 w-11 shrink-0 grid place-items-center rounded-full bg-primary text-primary-foreground hover:brightness-110 disabled:opacity-50"><Send className="w-5 h-5" /></button>
      </div>

      {history.length >= 2 && (
        <button onClick={getFeedback} disabled={loading} data-testid="practice-feedback-btn" className="w-full inline-flex items-center justify-center gap-2 rounded-full border-2 border-primary text-primary font-bold py-3 hover:bg-primary hover:text-primary-foreground transition disabled:opacity-50">
          <ClipboardCheck className="w-5 h-5" /> Analyse my performance
        </button>
      )}

      {feedback && (
        <div className="space-y-4 animate-rise">
          {feedback.scores && (
            <SectionCard title="Your scores">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {Object.entries(feedback.scores).map(([k, v]) => (
                  <div key={k} className="text-center rounded-md bg-secondary p-3">
                    <div className="font-head font-extrabold text-2xl text-primary">{v}</div>
                    <div className="text-[11px] text-muted-foreground capitalize">{k}</div>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}
          {feedback.what_went_well?.length > 0 && <SectionCard title="What went well"><Bullets items={feedback.what_went_well} /></SectionCard>}
          {feedback.what_to_improve?.length > 0 && <SectionCard title="What to improve"><Bullets items={feedback.what_to_improve} /></SectionCard>}
          {feedback.one_tip && <SectionCard title="One tip" accent><p>{feedback.one_tip}</p></SectionCard>}
        </div>
      )}
    </div>
  );
}
